import {
  MoneyIcon,
  BellIcon,
  GearIcon,
  ChartPieIcon,
  TrayIcon,
  MapPinIcon,
  TruckIcon,
  TruckTrailerIcon,
  UsersIcon,
  FileIcon,
  EyeIcon,
  CalendarIcon,
  LeafIcon,
  SmileySadIcon,
  PathIcon,
  PuzzlePieceIcon,
} from '@phosphor-icons/react';
import { useMemo } from 'react';

import { useAuth } from '../../contexts/auth-context';
import { useNotificationsCtx } from '../../app/notification/pages/context';
import { useMinimalGeneralSettings } from '../../contexts/minimal-settings-context';
import { roleToNumber } from '../../app/auth/roles.client';
import { InternalRole } from '../../app/auth/endpoints/MeEndpoint.types';
import { Sidebar } from 'components/Sidebar';
import { SidebarNavItem, SidebarUserItem, type ISidebarNavItemProps } from 'components/sidebar/SidebarNavItem';
import { ErrorStateBoundary } from '../error-state/ErrorStateBoundary';

export interface IDashboardProps {
  children: React.ReactNode;
}

type NavItem = Pick<ISidebarNavItemProps, 'to' | 'label' | 'icon' | 'count'>;

export const Dashboard: React.FC<IDashboardProps> = (props) => {
  const { children } = props;
  const { me } = useAuth();
  const {
    state: { hasUnreadNotifications },
  } = useNotificationsCtx();
  const { incomingDocCount } = useMinimalGeneralSettings();

  const [topItems, bottomItems] = useMemo(() => {
    const topRoutes: NavItem[] = [];
    const bottomRoutes: NavItem[] = [];

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalEmployeePlus)) {
      topRoutes.push({ label: 'Statistieken', to: '/internal/stats', icon: ChartPieIcon });
    }

    topRoutes.push(
      { label: 'Orders', to: '/internal/orders', icon: TrayIcon },
      { label: 'Planning', to: '/internal/planning-board', icon: CalendarIcon },
      { label: 'Klanten', to: '/internal/customers', icon: UsersIcon },
      { label: 'Vervoerders', to: '/internal/suppliers', icon: TruckIcon },
      { label: 'Trailers', to: '/internal/trailers', icon: TruckTrailerIcon },
      { label: 'Locaties', to: '/internal/locations', icon: MapPinIcon },
      { label: 'Inkomende docs', to: '/internal/incoming-docs', icon: FileIcon, count: incomingDocCount },
    );

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalEmployeePlus)) {
      topRoutes.push({ label: 'Financieel', to: '/internal/finance', icon: MoneyIcon });
    }

    topRoutes.push({ label: 'Sustainability', to: '/internal/sustainability', icon: LeafIcon });
    topRoutes.push({ label: 'Klachten', to: '/internal/complaints', icon: SmileySadIcon });

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      bottomRoutes.push({ label: 'Audit Logs', to: '/internal/audit-logs', icon: EyeIcon });
      bottomRoutes.push({ label: 'Tracking Logs', to: '/internal/tracking-logs', icon: PathIcon });
    }

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdminPlus)) {
      bottomRoutes.push({ label: 'Integraties', to: '/internal/integrations', icon: PuzzlePieceIcon });
      bottomRoutes.push({ label: 'Instellingen', to: '/internal/settings', icon: GearIcon });
    }

    return [topRoutes, bottomRoutes];
  }, [incomingDocCount, me.role]);

  return (
    <div className="flex min-h-screen w-full">
      <div className="shrink-0">
        <Sidebar
          homeLink="/internal/orders"
          topItems={
            <>
              <nav className="sidebar-nav">
                {topItems.map((item) => (
                  <SidebarNavItem key={item.to} {...item} />
                ))}
              </nav>

              {bottomItems.length > 0 && (
                <>
                  <div className="sidebar-divider" />
                  <nav className="sidebar-nav">
                    {bottomItems.map((item) => (
                      <SidebarNavItem key={item.to} {...item} />
                    ))}
                  </nav>
                </>
              )}
            </>
          }
          bottomItems={
            <nav className="sidebar-nav">
              <SidebarNavItem
                to="/internal/notifications"
                label="Notificaties"
                icon={BellIcon}
                hasIndicator={hasUnreadNotifications}
              />
              <SidebarUserItem to="/internal/me" name={me.name} imageUrl={me.image} />
            </nav>
          }
        />
      </div>

      <div className="pb-32 content-bg min-h-screen flex-1 min-w-0">
        <ErrorStateBoundary variant="page">{children}</ErrorStateBoundary>
      </div>
    </div>
  );
};
